/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
import * as Cesium from "cesium";
import React from "react";
import { TPanoramaVm } from "../../data";
import usePanoramaEntities from "../../pages/k-project/usePanoramaEntities";
import useCommonDataStore from "../../stores/common.store";
import useViewStore from "../../stores/viewer.store";

const svgContent = `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_333_334)">
<path d="M26.25 12.5C26.25 21.25 15 28.75 15 28.75C15 28.75 3.75 21.25 3.75 12.5C3.75 9.51631 4.93526 6.65483 7.04505 4.54505C9.15483 2.43526 12.0163 1.25 15 1.25C17.9837 1.25 20.8452 2.43526 22.955 4.54505C25.0647 6.65483 26.25 9.51631 26.25 12.5Z" fill="blue" stroke="#F3F3F3" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M15 16.25C17.0711 16.25 18.75 14.5711 18.75 12.5C18.75 10.4289 17.0711 8.75 15 8.75C12.9289 8.75 11.25 10.4289 11.25 12.5C11.25 14.5711 12.9289 16.25 15 16.25Z" fill="blue" stroke="#F3F3F3" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
</g>
<defs>
<clipPath id="clip0_333_334">
<rect width="30" height="30" fill="blue"/>
</clipPath>
</defs>
</svg>
`;

const svgUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`;

const usePanoramaPins = ({
  prefix,
  vm,
  show,
  zRatio = 10,
}: {
  prefix: string;
  vm: TPanoramaVm;
  show: boolean;
  zRatio?: number;
}) => {
  const { viewer, setPhase } = useViewStore();
  const { setCommonData } = useCommonDataStore();
  const { setEntities, pinClickHandler } = usePanoramaEntities({ viewer });

  const initPins = React.useCallback(() => {
    const entities = vm.items.map((p) => {
      return {
        id: `${prefix}:p${p.index}`,
        name: `${prefix}:p${p.index}`,
        position: Cesium.Cartesian3.fromDegrees(
          p.position[0],
          p.position[1],
          p.position[2],
        ),
        billboard: {
          image: svgUrl,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        },
        properties: p,
      };
    });
    setEntities(entities);

    // eslint-disable-next-line consistent-return
    return () => {
      setEntities([]);
    };
  }, [prefix, setEntities, vm.items]);

  const initEvents = React.useCallback(() => {
    let beforeClick: { id: { billboard: { color: Cesium.Color } } } | null =
      null;
    if (pinClickHandler && viewer) {
      pinClickHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pinClickHandler.setInputAction((click: any) => {
        const pickedObject = viewer.scene.pick(click.position);

        if (pickedObject) {
          // eslint-disable-next-line prefer-destructuring
          try {
            const { properties } = pickedObject.id;
            // eslint-disable-next-line no-underscore-dangle
            const p = properties._view._value.position;

            const cameraH = properties._cameraHeight
              ? properties._cameraHeight._value
              : p[2] + zRatio;

            const changePosition = Cesium.Cartesian3.fromDegrees(
              p[0],
              p[1],
              cameraH,
            );

            if (properties) {
              const { orientation } = properties._view._value;
              setPhase({
                // position,
                // orientation,
                position: [
                  changePosition.x,
                  changePosition.y,
                  changePosition.z,
                ],
                orientation: {
                  // heading: Cesium.Math.toRadians(properties.heading),
                  // pitch: Cesium.Math.toRadians(-13),
                  // roll: 0.0,
                  heading: Cesium.Math.toRadians(orientation.heading),
                  pitch: Cesium.Math.toRadians(orientation.pitch),
                  roll: Cesium.Math.toRadians(orientation.roll),
                },
              });

          
              setCommonData({ openIndex: `${prefix}:p${properties.index}` });

              if (beforeClick) {
                beforeClick.id.billboard.color = Cesium.Color.WHITE;
              }

              pickedObject.id.billboard.color = Cesium.Color.SKYBLUE;
              beforeClick = pickedObject;
            }
            // eslint-disable-next-line no-empty, @typescript-eslint/no-unused-vars
          } catch (e) {
            //   console.log(e);
          }
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
  }, [pinClickHandler, prefix, setCommonData, setPhase, viewer, zRatio]);

  const init = React.useCallback(() => {
    initPins();
    initEvents();
  }, [initEvents, initPins]);

  const clear = React.useCallback(() => {
    // clear initPins
    if (!viewer) return;
    viewer.entities.removeById("c4:p1");
    setEntities([]);
    // setCommonData({ ...commonData, openIndex: null });
    setCommonData({ openIndex: null });
  }, [viewer, setEntities, setCommonData]);

  React.useEffect(() => {
    if (!viewer || !show) return;
    init();
    // eslint-disable-next-line consistent-return
    return () => {
      clear();
    };
  }, [clear, init, show, viewer]);

  return { init, clear };
};

export default usePanoramaPins;
